import chevronLeft from "@assets/images/chevron-left.svg";
import EOrderingLogo from "@assets/images/eOrdering.png";
import EyeClose from "@assets/images/EyeClose.png";
import EyeOpen from "@assets/images/EyeOpen.png";
import SCGPLogo from "@assets/images/SCGP-color.svg";
import { Button, TextField } from "@material-ui/core";
import FormSpacer from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { IconButton } from "@saleor/macaw-ui";
import { SetPasswordData } from "@saleor/sdk";
import getAccountErrorMessage from "@saleor/utils/errors/account";
import classNames from "classnames";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "../styles";
import ValidateMessage from "./ValidateMessage";

const NewPasswordPageFormSchema = Yup.object().shape({
  password: Yup.string()
    .required("New password is required ")
    .min(8, "At least 8 characters")
    .matches(/[a-z]/, "A lowercase character")
    .matches(/[A-Z]/, "At least 8 characters ")
    .matches(/[0-9]/, "A number"),
  confirmPassword: Yup.string()
    .required("You'll need to confirm your new password")
    .oneOf(
      [Yup.ref("password"), null],
      "Confirm new passwords don't seem to match "
    )
});

export interface NewPasswordPageFormData {
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordPageProps {
  disabled: boolean;
  errors: SetPasswordData["errors"];
  errorCognito?: string;
  onSubmit: (data: NewPasswordPageFormData) => SubmitPromise;
  onBack: () => void;
}

export type ErrorsName = "minCharacters" | "lowercase" | "uppercase" | "number";

const ChangePasswordPage: React.FC<ChangePasswordPageProps> = props => {
  const { errors, errorCognito, onSubmit, onBack } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [errorPassword, setErrorPassword] = useState("");
  const [errorValidates, setErrorValidate] = useState<
    Record<ErrorsName, boolean>
  >(null);
  const intl = useIntl();
  const classes = useStyles(props);
  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: ""
    },
    validationSchema: NewPasswordPageFormSchema,
    onSubmit: values => {
      onSubmit(values);
    }
  });

  useEffect(() => {
    const errorPassword = getAccountErrorMessage(
      errors.find(err => err.field === "password"),
      intl
    );
    if (errorPassword) {
      setErrorPassword(errorPassword);
    }
  }, [errors]);

  const error = getAccountErrorMessage(
    errors.find(err => err.field !== "password"),
    intl
  );

  useEffect(() => {
    const passwordInput = formik.values.password;
    if (passwordInput) {
      setErrorValidate({
        lowercase: !/[a-z]/.test(passwordInput),
        uppercase: !/[A-Z]/.test(passwordInput),
        number: !/[0-9]/.test(passwordInput),
        minCharacters: !(passwordInput.length >= 8)
      });
      return;
    }
    setErrorValidate(null);
  }, [formik.values.password]);

  useEffect(() => {
    if (errorPassword) {
      setErrorPassword("");
    }
  }, [formik.values.password]);

  return (
    <div className={classes.root}>
      <div className={classes.mainPanel}>
        <div className={classes.headTextLogo}>
          <div className={classes.text}>
            <img src={SCGPLogo} alt={"logo"} />
          </div>
          <div className={classes.logo}>
            <img src={EOrderingLogo} alt={"logo"} />
          </div>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <MyTypography variant="h5" className={classes.header}>
            <FormattedMessage
              defaultMessage="Change Password"
              description="page title"
            />
          </MyTypography>
          <MyTypography variant="body3" className={classes.description}>
            <FormattedMessage defaultMessage="กรุณาเปลี่ยน Password ใหม่ เมื่อเริ่มต้นใช้งานครั้งแรก" />
          </MyTypography>
          <FormSpacer space={16} />
          {error && (
            <MyTypography className={classes.textError} variant="body3">
              {error}
            </MyTypography>
          )}
          {errorCognito && (
            <MyTypography className={classes.textError} variant="body3">
              {errorCognito}
            </MyTypography>
          )}
          <FormSpacer space={16} />
          <div className={classes.passwordWrapper}>
            <TextField
              autoFocus
              fullWidth
              autoComplete="none"
              label={intl.formatMessage({
                defaultMessage: "New Password"
              })}
              error={
                (formik.errors.password && formik.touched.password) ||
                !!errorPassword
              }
              name="password"
              onChange={formik.handleChange}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              inputProps={{
                "data-testid": "password"
              }}
            />
            <MyTypography className={classes.textError} variant="caption">
              {formik.errors.password &&
              formik.touched.password &&
              !formik.values.password
                ? formik.errors.password
                : ""}
            </MyTypography>
            {errorPassword && (
              <MyTypography className={classes.textError} variant="caption">
                {errorPassword}
              </MyTypography>
            )}
            <ValidateMessage errors={errorValidates} />
            <IconButton
              className={classes.showPasswordBtn}
              variant="secondary"
              hoverOutline={false}
              onClick={() => setShowPassword(!showPassword)}
            >
              <img
                src={showPassword ? EyeOpen : EyeClose}
                alt="Set show password input field"
              />
            </IconButton>
          </div>
          <FormSpacer />
          <div className={classes.passwordWrapper}>
            <TextField
              fullWidth
              error={
                formik.errors.confirmPassword && formik.touched.confirmPassword
              }
              autoComplete="none"
              label={intl.formatMessage({
                defaultMessage: "Confirm New Password"
              })}
              name="confirmPassword"
              onChange={formik.handleChange}
              type={showConfirmPassword ? "text" : "password"}
              value={formik.values.confirmPassword}
              inputProps={{
                "data-testid": "confirm-password"
              }}
            />
            <MyTypography className={classes.textError} variant="caption">
              {formik.errors.confirmPassword && formik.touched.confirmPassword
                ? formik.errors.confirmPassword
                : ""}
            </MyTypography>
            <IconButton
              className={classes.showPasswordBtn}
              variant="secondary"
              hoverOutline={false}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              <img
                src={showConfirmPassword ? EyeOpen : EyeClose}
                alt="Set show confirm password input field"
              />
            </IconButton>
          </div>
          <FormSpacer />
          <Button
            data-testid="button-bar-confirm"
            className={classNames(
              classes.typeButton,
              "gradient",
              classes.typeButtonSCG
            )}
            variant="contained"
            type="submit"
          >
            <MyTypography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Change Password"
                description="button"
              />
            </MyTypography>
          </Button>
          <MyTypography
            className={classes.linkBack}
            onClick={onBack}
            variant="body3"
            data-testid="login-link"
          >
            <img src={chevronLeft} alt="Back to Sign In" />
            <FormattedMessage
              defaultMessage="Back to Sign In"
              description="description"
            />
          </MyTypography>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
